/* Cycle House | Progressive infinite scroll for Pipeline collection grids.
   Requires data-ch-infinite on the collection__products element,
   data-ch-pagination on its existing pagination wrapper and
   data-ch-infinite-next with data-next-url on the load-more element.
*/
(() => {
  'use strict';

  const selector = '[data-section-type="collection"] [data-products-grid][data-ch-infinite]';
  const active = new Map();
  let refreshQueued = false;

  function refresh() {
    refreshQueued = false;

    for (const [container, state] of active) {
      if (!container.isConnected ||
          container.querySelector('.js-grid') !== state.grid ||
          container.querySelector('[data-ch-infinite-next]') !== state.trigger) {
        state.observer.disconnect();
        state.controller?.abort();
        active.delete(container);
        container.classList.remove('ch-infinite-ready');
      }
    }

    document.querySelectorAll(selector).forEach((container) => {
      if (active.has(container)) return;

      const grid = container.querySelector('.js-grid');
      const trigger = container.querySelector('[data-ch-infinite-next]');
      if (!grid || !trigger || !trigger.dataset.nextUrl || !('IntersectionObserver' in window)) return;

      const button = trigger.querySelector('[data-ch-load-button]');
      const status = trigger.querySelector('[data-ch-load-status]');
      const state = {
        grid, trigger, button, status,
        busy: false, complete: false, controller: null, observer: null
      };

      const setBusy = (busy) => {
        state.busy = busy;
        if (button) {
          button.disabled = busy;
          button.textContent = busy ? 'Loading more bikes & accessories…' : 'Load More Products';
        }
        if (status) status.textContent = busy ? 'Loading more products' : '';
      };

      const showFallback = (message) => {
        container.classList.remove('ch-infinite-ready');
        state.observer.disconnect();
        if (status) status.textContent = message;
        if (button) button.hidden = true;
      };

      async function loadNext() {
        if (state.busy || !trigger.isConnected || !trigger.dataset.nextUrl) return;

        const pageKey = window.location.pathname + window.location.search;
        const nextPage = new URL(trigger.dataset.nextUrl, window.location.href);
        if (nextPage.origin !== window.location.origin) return;

        const section = container.closest('[data-section-id]');
        const sectionId = section?.getAttribute('data-section-id');

        const controller = new AbortController();
        state.controller = controller;
        state.observer.unobserve(trigger);
        setBusy(true);

        try {
          async function fetchMarkup(sectionOnly) {
            const target = new URL(nextPage.toString());
            if (sectionOnly && sectionId) target.searchParams.set('section_id', sectionId);
            const response = await fetch(target.toString(), {
              credentials: 'same-origin',
              signal: controller.signal,
              headers: { Accept: 'text/html' }
            });
            if (!response.ok) throw new Error('HTTP ' + response.status);
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            return {
              grid: html.querySelector('[data-products-grid] .js-grid'),
              next: html.querySelector('[data-products-grid] [data-ch-infinite-next]')
            };
          }

          // Shopify's Section Rendering API is faster than loading the entire page.
          let result = await fetchMarkup(true);
          if (!result.grid) result = await fetchMarkup(false);
          if (!result.grid) throw new Error('Product grid missing from response');

          // If a filter/sort AJAX update replaced the grid, discard this result.
          if (!container.isConnected || container.querySelector('.js-grid') !== grid ||
              container.querySelector('[data-ch-infinite-next]') !== trigger ||
              pageKey !== window.location.pathname + window.location.search) return;

          const newCards = Array.from(result.grid.children);
          if (!newCards.length) throw new Error('No product cards found');

          const fragment = document.createDocumentFragment();
          newCards.forEach((card) => fragment.appendChild(card));
          grid.appendChild(fragment);

          const followingUrl = result.next?.dataset.nextUrl || '';
          if (followingUrl) {
            trigger.dataset.nextUrl = followingUrl;
          } else {
            // Important: do NOT remove the trigger. Pipeline re-renders the
            // grid after appending cards, and removing it makes refresh()
            // remove ch-infinite-ready, revealing the old pagination again.
            state.complete = true;
            trigger.removeAttribute('data-next-url');
            state.observer.disconnect();
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('[Cycle House infinite scroll]', error);
            showFallback('Could not load more products. Please use the page links below.');
          }
        } finally {
          if (state.complete) {
            state.busy = false;
            if (button) {
              button.hidden = true;
              button.disabled = true;
            }
            if (status) status.textContent = 'You have viewed all products';
          } else {
            setBusy(false);
            if (trigger.isConnected && trigger.dataset.nextUrl &&
                container.classList.contains('ch-infinite-ready')) {
              state.observer.observe(trigger);
            }
          }
        }
      }

      state.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNext();
      }, { rootMargin: '650px 0px', threshold: 0 });

      button?.addEventListener('click', loadNext);
      active.set(container, state);
      container.classList.add('ch-infinite-ready');
      state.observer.observe(trigger);
    });
  }

  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(refresh);
  }

  function boot() {
    refresh();
    // Pipeline can replace the product grid when filters or sorting change.
    // Rebind when a new grid/pagination node is inserted.
    const changes = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.type !== 'childList') return false;
        if (record.target.closest?.('[data-products-grid]')) return true;
        return Array.from(record.addedNodes).some((node) =>
          node.nodeType === 1 && (
            node.matches?.('[data-products-grid], [data-section-type="collection"]') ||
            node.querySelector?.('[data-products-grid]')
          )
        );
      });
      if (relevant) queueRefresh();
    });
    changes.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
